import {
  CropStatusEnum,
  GrainUsageTypeEnum,
  ROUTE_MEASURE_GRAIN,
} from '@/common/constants';
import usePartyGrainStore from '@/features/parties-grain/store/usePartyGrainStore';
import useStore from '@/lib/store';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useMemo, useState } from 'react';
import { SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { generateGrainPDF } from '../components/general-grain';
import { GeneralGrainSchema } from '../components/general/schemas';
import { ILandMergeData, ILoadGeneral } from '../models';
import { useLoadStore } from '../store';

export const useCreateGrainReception = () => {
  const { t } = useTranslation();
  const { user } = useStore();
  const navigate = useNavigate();
  const [contractList, setContractList] = useState<any[]>([]);
  const [mergedTypeList, setMergedTypeList] = useState<any>([]);
  const [load, setLoad] = useState<any>();
  const [cropStatus, setCropStatus] = useState(
    CropStatusEnum.HAS_LAND_MERGE_DATA,
  );
  const [dealType, setDealType] = useState(undefined);

  const { fetchFarmerGrain, saveGrainReception, receptionGrainLoading } =
    useLoadStore();
  const {
    cropTypes,
    plantsGrain,
    storageTypes,
    cropGenerations,
    cropTypesByPlant,
    fetchPlantsGrain,
    fetchCropTypesByPlant,
    cropGenerationsByPlantId,
    fetchCropGenerationsByPlantId,
  } = usePartyGrainStore();

  const form = useForm<ILoadGeneral | any>({
    resolver: yupResolver(GeneralGrainSchema),
    context: { cropStatus, dealType },
    defaultValues: {
      tax_number: '',
    },
  });

  const { setValue, watch } = form;

  const storageType = useWatch({
    control: form.control,
    name: 'storage_type',
  });

  const plantId = useWatch({
    name: 'plant_id',
    control: form.control,
  });

  useEffect(() => {
    fetchPlantsGrain();
  }, []);

  useEffect(() => {
    if (plantId) {
      fetchCropTypesByPlant(String(plantId));
      fetchCropGenerationsByPlantId(String(plantId));
    }
  }, [plantId]);

  const getFarmer = async () => {
    const inn = form.getValues('tax_number');
    form.reset({
      tax_number: inn,
      farmer_contract_id: '',
      farmer_name: '',
      buyer_tin: '',
      contract_amount: undefined,
      reception_weight: undefined,
      organization_name: '',
    });

    try {
      const farmer = await fetchFarmerGrain!(inn);
      setValue('farmer_name', farmer?.[0]?.farmer_name);
      // setValue('organization_name', farmer?.[0]?.organization_name);

      setContractList(
        farmer?.map((el: any) => ({
          label: el?.contract_number,
          value: el.id,
          ...el,
        })),
      );
    } catch (error) {
      console.error('Failed to fetch farmer:', error);
    }
  };

  useEffect(() => {
    if (watch('tax_number').length === 9) getFarmer();
  }, [watch('tax_number').length === 9]);

  useEffect(() => {
    if (!watch('farmer_contract_id')) return;
    const contract = contractList?.find(
      (el) => el.value === watch('farmer_contract_id'),
    );

    setValue('crop_data', '');
    setValue('crop_generation_id', '');
    setValue('crop_type_id', '');
    setValue('organization_name', contract?.organization_name);
    setValue('contract_amount', contract?.harvest_plan ?? undefined);
    setValue('reception_weight', contract?.reception_weight ?? undefined);
    setValue('plant_id', contract?.plant_id);
    setValue('storage_type', contract?.contract_type);
    if (contract?.harvest_plan != null && contract?.reception_weight != null) {
      setValue(
        'residual_amount',
        String(
          Number(contract.harvest_plan) - Number(contract.reception_weight),
        ),
        { shouldValidate: true, shouldDirty: true },
      );
    } else {
      setValue('residual_amount', '');
    }

    setValue('buyer_tin', contract?.buyer_tin);
    setValue('buyer_name', contract?.buyer_name);
    setValue('area', contract?.area);

    if (contract?.land_merge_data?.length === 1) {
      const cropType = cropTypes?.find(
        (type) =>
          type.value == String(contract.land_merge_data?.at(0)?.crop_type_id),
      );
      const cropGeneration = cropGenerations?.find(
        (gen) =>
          gen.value == contract.land_merge_data?.at(0)?.crop_generation_id,
      );

      if (cropType && cropGeneration) {
        setMergedTypeList([
          {
            value: `${cropType.value},${cropGeneration.value}`,
            label: `${cropType?.label} / ${cropGeneration?.label}`,
          },
        ]);
        setCropStatus(CropStatusEnum.HAS_LAND_MERGE_DATA);
      } else if (
        (cropGeneration && !cropType) ||
        (cropType && !cropGeneration)
      ) {
        setCropStatus(CropStatusEnum.NOT_HAS_LAND_MERGE_DATA);
      }
    } else if (contract?.land_merge_data?.length > 1) {
      const arr: any = [];
      contract?.land_merge_data.forEach((el: ILandMergeData) => {
        const cropType = cropTypes?.find(
          (type) => type.value == String(el?.crop_type_id),
        );
        const cropGeneration = cropGenerations?.find(
          (gen) => gen.value == el?.crop_generation_id,
        );

        if (cropType && cropGeneration) {
          arr.push({
            value: `${cropType.value},${cropGeneration.value}`,
            label: `${cropType?.label} / ${cropGeneration?.label}`,
          });
        }
      });

      if (arr.length > 0) {
        setMergedTypeList(arr);
        setCropStatus(CropStatusEnum.HAS_LAND_MERGE_DATA);
      }
    } else {
      setCropStatus(CropStatusEnum.NOT_HAS_LAND_MERGE_DATA);
    }
  }, [watch('farmer_contract_id')]);

  const contract = useMemo(() => {
    setDealType(
      contractList.find((el) => el.id === watch('farmer_contract_id')),
    );
    return contractList.find((el) => el.id === watch('farmer_contract_id'));
  }, [watch('farmer_contract_id')]);

  useEffect(() => {
    if (!watch('crop_data')) return;
    setValue('crop_type_id', watch('crop_data')?.split(',')[0]);
    setValue('crop_generation_id', watch('crop_data')?.split(',')[1]);
  }, [watch('crop_data')]);

  const generateDocs = async () => {
    await generateGrainPDF(
      contract!,
      user!,
      load?.number,
      load?.created_at,
      load?.farmer_tax_number,
    );
    navigate(`${ROUTE_MEASURE_GRAIN}/${load?.id}`);
  };

  const saveForm: SubmitHandler<ILoadGeneral | any> = async (data) => {
    const payload: any = {
      cluster_id: user?.cluster_id,
      farmer_contract_id: data?.farmer_contract_id,
      plant_id: data?.plant_id,
      farmer_tax_number: data?.tax_number,
      plant_name: plantsGrain?.find((el) => el?.value === data?.plant_id)
        ?.label,
      storage_type: data?.storage_type,
      usage_type:
        data?.storage_type === GrainUsageTypeEnum.SEED
          ? GrainUsageTypeEnum.SEED
          : GrainUsageTypeEnum.GOODS,
      crop_type_id: data?.crop_type_id,
      // area: data?.area,
    };

    if (data?.storage_type === GrainUsageTypeEnum.SEED) {
      payload.crop_generation_id = data?.crop_generation_id;
    }

    try {
      const result = await saveGrainReception(payload);
      setLoad(result);
    } catch (error) {
      console.error('Receptionni saqlashda xatolik:', error);
    }
  };

  return {
    form,
    load,
    contract,
    saveForm,
    plantsGrain,
    storageTypes,
    storageType,
    contractList,
    generateDocs,
    cropTypesByPlant,
    receptionGrainLoading,
    cropGenerationsByPlantId,
  };
};
